import { useEffect, useRef, useState } from 'react';
import { Tooltip } from 'react-tooltip';
import styled from 'styled-components';
import { Dropdown } from '../Dropdown';

export const DropdownInput = ({
  value,
  placeholder,
  options,
  onChange,
  onClick,
  label,
  iconClassName,
  id,
  tooltip,

  isDisabled,
  isSearchable = true,
  canBeCleared = true,
  isArrowVisible = true,
  horizontalAlign,
  textColor,
  textAlign,
  style,
}: {
  value?: string | number | boolean | null;
  placeholder?: string | number | boolean;
  options: {
    value: string | number | boolean | undefined;
    label: string;
    shortLabel?: string;
    iconClassName?: string;
  }[];
  onChange: (newValue?: string | number | boolean) => void;
  onClick?: () => void;
  label?: string;
  iconClassName?: string;
  id?: string;
  tooltip?: string;

  isDisabled?: boolean;
  isSearchable?: boolean;
  canBeCleared?: boolean;
  isArrowVisible?: boolean;
  horizontalAlign?: 'left' | 'right' | 'center';
  textColor?: string;
  textAlign?: 'left' | 'right' | 'center';
  style?: React.CSSProperties;
}) => {
  const ref = useRef<HTMLInputElement>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isMouseOver, setIsMouseOver] = useState(false);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const filteredOptions = options?.filter(
    (option) => option.label.includes(searchTerm) || String(option.value)?.includes(searchTerm)
  );
  const [optionIconClassName, setOptionIconClassName] = useState<string | undefined>();

  const valueToLabel = (value?: string | number | boolean) => {
    const option = options.find((option) => option.value === value);
    return option?.shortLabel || option?.label || '';
  };
  const labelToValue = (label?: string) => {
    const option = options.find((option) => option.label === label);
    return option?.value;
  };

  const indexToValue = (index?: number | null) => {
    if (index === undefined || index === null) return undefined;
    const value = filteredOptions[index]?.value;
    return value;
  };

  useEffect(() => {
    if (searchTerm) {
      setHoveredIndex(0);
      setIsDropdownOpen(true);
    } else {
      setHoveredIndex(null);
    }
  }, [searchTerm]);

  useEffect(() => {
    const optionClassName = options.find((option) => option.value === value)?.iconClassName;

    setOptionIconClassName(optionClassName);
  }, [value]);

  return (
    <Container
      onMouseEnter={() => setIsMouseOver(true)}
      onMouseLeave={() => setIsMouseOver(false)}
      style={style}
      onClick={onClick}
      $isDisabled={isDisabled}
    >
      {(label || iconClassName) && (
        <Label
          id={id}
          onClick={() => {
            isSearchable && ref.current?.focus();
          }}
        >
          {iconClassName && <i className={iconClassName} />}
          {label}
        </Label>
      )}
      {tooltip && id && <Tooltip anchorSelect={`#${id}`} place="bottom" content={tooltip} delayShow={1000} />}

      <Dropdown
        horizontalAlign={horizontalAlign}
        style={{ width: '100%' }}
        contentStyle={{ minWidth: '100%' }}
        onOpen={(open) => !open && setHoveredIndex(null)}
        isOpen={isDropdownOpen}
        setOpen={setIsDropdownOpen}
        trigger={
          <TriggerContainer>
            {optionIconClassName && <i style={{ color: textColor }} className={optionIconClassName} />}
            <InputField
              $textColor={textColor}
              style={{
                pointerEvents: isSearchable ? 'auto' : 'none',
                textAlign: textAlign,
                opacity: value === null && ref.current?.value === '' ? 0.3 : 1,
              }}
              disabled={!isSearchable}
              ref={ref}
              type="text"
              placeholder={value === null ? 'mixed' : valueToLabel(value === undefined ? placeholder : value)}
              onFocus={(e) => {
                e.target?.select();
              }}
              onChange={(e) => {
                setSearchTerm(e.target.value);

                const optionValue = labelToValue(e.target.value);
                optionValue && onChange(optionValue);
              }}
              onKeyDown={(e) => {
                if (e.code === 'ArrowDown') {
                  if (isDropdownOpen === false) setIsDropdownOpen(true);
                  setHoveredIndex((prev) => {
                    if (prev === null) {
                      return 0;
                    } else {
                      if (filteredOptions?.length - 1 === prev) return prev;
                      return prev + 1;
                    }
                  });
                }
                if (e.code === 'ArrowUp')
                  setHoveredIndex((prev) => {
                    if (prev === null) {
                      return filteredOptions?.length - 1;
                    } else {
                      if (prev === 0) return prev;
                      return prev - 1;
                    }
                  });

                if (e.code === 'Enter') {
                  const newValue = indexToValue(hoveredIndex);

                  newValue && onChange(newValue);
                  if (ref.current && newValue !== value) {
                    ref.current.placeholder = '';
                    ref.current.value = '';
                  }
                  setSearchTerm('');
                  setIsDropdownOpen(false);
                }
              }}
              spellCheck={false}
              $isComputed={!ref.current?.value && value === undefined && placeholder !== undefined}
            />

            {canBeCleared && (
              <ClearIcon
                $isVisible={(value !== undefined || searchTerm !== '') && isMouseOver}
                onClick={(e) => {
                  e.stopPropagation();
                  setSearchTerm('');
                  if (ref.current) {
                    ref.current.placeholder = '';
                    ref.current.value = '';
                  }
                  setIsDropdownOpen(false);
                  onChange(undefined);
                }}
              >
                <i className="icon-x" />
              </ClearIcon>
            )}

            {isArrowVisible && (
              <ArrowIcon
                onClick={(e) => {
                  e.stopPropagation();
                  setIsDropdownOpen((prev) => !prev);
                }}
                $isOpen={isDropdownOpen}
              >
                <i className="icon-arrow-right" />
              </ArrowIcon>
            )}
          </TriggerContainer>
        }
      >
        {filteredOptions?.length > 0 && (
          <DropdownContentContainer>
            {filteredOptions?.map(({ value, shortLabel, label, iconClassName }, index) => (
              <DropdownContentItem
                style={{ justifyContent: textAlign }}
                key={`dropdown-item-${value}`}
                onClick={() => {
                  if (ref.current) {
                    ref.current.placeholder = shortLabel || label;
                    ref.current.value = '';
                  }
                  setSearchTerm('');
                  onChange(value);
                }}
                $isHovered={hoveredIndex === index}
              >
                {iconClassName && <i className={iconClassName} />}
                <span>{label}</span>
              </DropdownContentItem>
            ))}
          </DropdownContentContainer>
        )}
      </Dropdown>
    </Container>
  );
};

const Container = styled.div<{ $isDisabled?: boolean }>`
  box-sizing: border-box;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 0 8px;

  border-radius: var(--radius-2);

  &:hover {
    outline: 1px solid var(--neutral-3);
  }

  &:focus-within {
    outline: 1px solid var(--primary-1);
  }

  ${({ $isDisabled }) =>
    $isDisabled &&
    `
    pointer-events: none;
  `}
`;

const Label = styled.label`
  padding-right: 5px;

  color: var(--main-2);
  font-family: var(--font-2);
  font-size: 13px;
  white-space: nowrap;

  display: flex;
  align-items: center;

  i {
    width: 20px;
    font-size: 19px;
    translate: -2px 0;
  }
`;

const InputField = styled.input<{ $isComputed?: boolean; $textColor?: string }>`
  cursor: default;
  box-sizing: border-box;
  width: 100%;
  height: 25px;

  overflow: hidden;
  text-overflow: ellipsis;

  background-color: transparent;
  border: none;

  color: var(--primary-1);
  font-family: var(--font-1);
  font-size: 11px;
  font-weight: 300;

  &:focus {
    outline: none;
  }

  &::selection {
    background: var(--primary-2);
    color: var(--main-1);
  }

  &::placeholder {
    color: ${({ $textColor }) => $textColor || 'var(--main-1)'};
    font-size: 11px;
    font-weight: 300;
  }

  ${({ $isComputed }) =>
    $isComputed &&
    `
    color: var(--main-2);
    font-style: italic;

    &::placeholder {
      color: var(--main-2);
      font-style: italic;
    }
  `}
`;

const ArrowIcon = styled.div<{ $isOpen?: boolean }>`
  height: 100%;
  display: flex;
  align-items: center;
  rotate: 90deg;

  font-size: 10px;

  &:hover {
    color: var(--main-1);
  }

  transition: rotate var(--transition-1);
  ${({ $isOpen }) => $isOpen && `rotate: -90deg;`}
`;

const ClearIcon = styled.div<{ $isVisible?: boolean }>`
  box-sizing: border-box;
  height: 100%;
  padding: 5px;
  margin-right: 5px;
  display: flex;
  align-items: center;

  font-size: 10px;
  border-radius: var(--radius-2);

  &:hover {
    background-color: var(--neutral-3);
    color: var(--main-1);
  }

  opacity: 0;
  pointer-events: none;
  ${({ $isVisible }) => $isVisible && `opacity: 1; pointer-events: auto;`}
`;

const TriggerContainer = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
`;

const DropdownContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 5px;

  background-color: var(--neutral-2);
  border: 1px solid var(--neutral-3);
  border-radius: var(--radius-2);

  box-shadow: var(--shadow-1);
`;

const DropdownContentItem = styled.div<{ $isHovered?: boolean }>`
  cursor: pointer;
  padding: 5px 10px;
  white-space: nowrap;

  display: flex;
  justify-content: space-between;

  font-size: 11px;
  background-color: var(--neutral-2);
  color: var(--main-2);
  border-radius: var(--radius-2);

  transition:
    background-color 0.3s ease,
    color 0.3s ease;

  &:hover {
    background-color: var(--neutral-3);
    color: var(--main-1);
  }

  ${({ $isHovered }) => $isHovered && `background-color: var(--neutral-3); color: var(--main-1);`}
`;
